import React from 'react';
import { Link } from '@nerdwallet/currency';

export default {
  content: [
    {
      title: '1. Scope',
      content: `This Privacy Policy applies to all job candidates who apply for one or more NerdWallet positions. If the candidate is offered and accepts employment from NerdWallet, the NerdWallet Employee Privacy Policy will apply to the collection and use of the candidate’s personal information.`,
    },
    {
      title: '2. Overview',
      content: `NerdWallet takes its responsibility to safeguard and protect the personal information of our candidates seriously. This Privacy Policy describes NerdWallet’s policies and practices regarding its collection and use of your personal information, and your rights with respect to the same.
        
        In this Privacy Policy, the terms “NerdWallet”, “we”, “us”, and “our” refers to NerdWallet Inc., its affiliates and subsidiaries globally. Each legal entity within the NerdWallet group is a data controller for purposes of its candidates’ personal information.`,
    },
    {
      title: '3. Defining Personal Information',
      content: `For purposes of this Privacy Policy, personal information means any information that:`,
      list: [
        'Directly identifies you, or',
        'Can be used in combination with other information to identify you.',
      ],
    },
    {
      title: '4. Security & Data Protection',
      content: `To help protect the privacy of personal information in our care, NerdWallet maintains reasonable and appropriate administrative, physical, and electronic safeguards. We restrict access to your personal information to only those who need to know (such as the human resources organization and hiring managers).
        
      We train employees about the importance of confidentiality and keeping your information private and secure. We contractually require our service providers to use appropriate measures to protect the confidentiality and security of your personal information. In addition, when we share personal information with third parties with whom we are contractually engaged, we require them to use the information only in a manner necessary to provide the service(s) and include data protection obligations consistent with this Privacy Policy and applicable law.`,
    },
    {
      title: '5. Data Integrity & Purpose Limitation',
      content: (
        <React.Fragment>
          We will take reasonable steps to ensure we maintain the accuracy and
          completeness of the personal information you provide us so that we can
          process it reliably. If you need to, You can always correct your
          personal information to help assure the integrity and accuracy of the
          information you provide us by contacting us at{' '}
          <Link href="mailto:privacy@NerdWallet.com">
            privacy@NerdWallet.com
          </Link>
          .
          <br />
          <br />
          NerdWallet will not use your personal information for any purpose that
          is incompatible with this Privacy Policy unless
        </React.Fragment>
      ),
      list: [
        'It is required by law;',
        'Separately authorized by you; or',
        'Is in your own vital interest (e.g. in the case of a medical emergency).',
      ],
    },
    {
      title: '6. Your Personal Information - Why, How, What, and Who',
      secondLevelContent: [
        {
          title: 'A. Why Does NerdWallet Need This Information?',
          content:
            'NerdWallet collects and processes your data for the following reasons:',
          list: [
            `It is necessary to enter or potentially enter into a contract
          with us. Either you have asked for something to be done so you
          can enter into an employment contract with us (such as a job
          application) or it is necessary for actually entering into an
          employment contract with you.`,
            `The processing is necessary under a legal obligation (for
            example, the voluntary collection of gender identification
            data in California).`,
            `The processing is necessary to protect your vital interests
            (see Data Integrity & Purpose Limitation above).`,
            `NerdWallet’s legitimate interests, including but not limited
            to improving the diversity and inclusivity of our applicant
            pipeline and tracking our efforts towards our Diversity,
            Equity and Inclusion initiatives.`,
          ],
          bottomContent: `With the exception of certain information that is necessary to
          fulfill our contractual or legal obligations, your decision to
          provide personal information is voluntary. If you choose not to
          provide certain information, we may not be able to process your
          job application.`,
        },
        {
          title: 'B. How Does NerdWallet Collect this Information?',
          content: `Generally, we collect personal information directly from you (such as during the application process). However, in some instances, the personal information we collect has been inferred about you based on other information you provide us, through your working relationship with NerdWallet, or from third parties (e.g. LinkedIn).
            
          When we collect your personal information from third parties and not from you, it is because:`,
          list: [
            `You’ve given us express consent to do so; or`,
            `Your consent is clearly implied by your actions (e.g., you use
                a third-party candidateservice available to you through
                NerdWallet); or`,
            `You provided explicit or implied consent to a third party to
            share the information with us (e.g., employment search firm,
            temporary agencies, public social networks, etc.)`,
          ],
        },
        {
          title: 'C. What Personal Information Do We Collect?',
          content: `We collect and process different types of personal information about you in accordance with applicable law. Typical information, which may depend on your location, includes:
          
          Human Resources Data`,
          list: [
            `Your resume or CV, cover letter, previous and/or relevant work
          experience or otherexperience, education, transcripts, or
          other information you provide to us in support of an
          application and/or the application and recruitment process,
          including information from references.`,
            `Name, gender and/or sex, home address and telephone number,
          email address, and date of birth.`,
            `Residency and work permit status, military status,
          nationality, citizenship, veteran status,disability status,
          language, and passport information.`,
            `Social security or other taxpayer/government identification
          number.`,
            `Payroll/wage expectations.`,
            `Date of hire, work history, technical skills, educational
          background, professionalcertifications and registrations, and
          required skills to complete the essential functions of the
          position.`,
            `Demographic information like your age, disability status,
          marital status, sexual orientation, gender identity, and race.`,
          ],
        },
        {
          title: 'D. What Do We Do With Your Personal Information?',
          content: `NerdWallet uses your personal information in order to manage all aspects of your potential employment relationship with us. The types of activities include:`,
          thirdLevelContent: [
            {
              title: 'i. Managing Workforce',
              content: `This means evaluating and selecting the workforce (which may require verification of work authorization before the job candidate receives an offer). In the case of a successful candidacy, the relevant employee Privacy Policy will apply.`,
            },
            {
              title: 'ii. Compliance Functions',
              content: `This means complying with legal and other requirements. This generally includes judicial or administrative orders regarding individuals (e.g. garnishments, child support payment), conflict of interest reporting, audits and risk management, immigration visa or work permit applications, employment and tax law, compliance with government inspections, reporting, and other requests from government or other public authorities, responding to legal process such as subpoenas, pursuing legal rights and remedies, defending litigation and complying with internal initiatives, policies and procedures.`,
            },
            {
              title: 'iii. Internal Programs and Initiatives',
              content: `This means analyzing our hiring process to determine if our diversity and inclusion efforts are reaching all segments of the population, consistent with our goals. Our efforts include designing hiring processes that promote equity and inclusion while mitigating bias, expanding our candidate pipelines, and ensuring an inclusive candidate experience.`,
            },
          ],
        },
        {
          title: 'E. Who Do We Share Your Information With?',
          content: `Your personal information will be shared with Human Resources, hiring managers and NerdWallet staff who may participate in the interview and offer process. If voluntarily provided by you, demographic data will be aggregated and shared with our People team for public reporting. If you accept a position, we may also share this information internally with relevant teams, including but not limited to HR, IT, Benefits, Compliance and Legal.
          
          The types of unaffiliated third parties we may share your information with are:`,
          thirdLevelContent: [
            {
              title: 'i. Service Providers',
              content: `This typically includes services such as payroll, pension schemes, benefits providers, human resources services, performance and engagement management, IT systems suppliers and support, third parties assisting with equity compensation programs, medical or health practitioners, trade bodies and associations, and other service providers.`,
            },
            {
              title: 'ii. Professional Advisors',
              content: `These are professionals who provide expert guidance and advice to NerdWallet in all of the countries where NerdWallet operates. Professional advisors generally include bankers, attorneys, accountants, auditors, lawyers, and insurers.`,
            },
            {
              title: 'iii. Public and Governmental Authorities',
              content: `These are entities that regulate or have jurisdiction over NerdWallet. These entities include regulatory authorities, law enforcement, public bodies, and judicial bodies.`,
            },
            {
              title: 'iv. Corporate Transactions',
              content: `We may buy or sell businesses and other assets. In such transactions, employee information is generally one of the transferred business assets. We reserve the right to include your personal information as an asset in any such transfer. Also, in the event that we, or substantially all of our assets, are required, your personal information may be one of the transferred assets.`,
            },
          ],
        },
      ],
    },
    {
      title: '7. Your Rights',
      content: `Please keep your personal information up to date and inform us of any significant changes to your personal information.
      
      You can access, correct, or delete personal information as described below. Your ability to access, correct or delete personal information is not absolute. There are instances where applicable law or regulatory requirements allow or require us to refuse to provide some or all of the personal information we hold about you. In addition, the personal information may have been destroyed, erased or made anonymous.`,
      secondLevelContent: [
        {
          title: 'A. Accessing Your Personal Information',
          content: (
            <React.Fragment>
              You can access your personal information via your Greenhouse job
              post.If there is personal information you want to review, verify
              or correct that is outside Greenhouse but is in NerdWallet’s
              control, please contact{' '}
              <Link href="mailto:privacy@NerdWallet.com">
                privacy@NerdWallet.com
              </Link>{' '}
              to request the revision or correction.
            </React.Fragment>
          ),
        },
        {
          title: 'B. Correcting Your Personal Information',
          content: (
            <React.Fragment>
              If you believe your personal information is incorrect, incomplete,
              or outdated, you can usually correct that information via your job
              post in Greenhouse. If the information is in a system that does
              not provide you direct access, you may request the revision or
              correction of that information by contacting{' '}
              <Link href="mailto:privacy@NerdWallet.com">
                privacy@NerdWallet.com
              </Link>
              .
              <br />
              <br />
              If it is determined that personal information is inaccurate,
              incomplete or outdated, NerdWallet will revise it. If necessary,
              we will also inform agents, service providers or other third
              parties who may also have this information so records in their
              possession may be corrected or updated. We reserve the right not
              to change any personal information NerdWallet considers accurate
            </React.Fragment>
          ),
        },
        {
          title: 'C. Deleting Your Personal Information',
          content: (
            <React.Fragment>
              You can request we delete the personal information that NerdWallet
              holds about you.
              <br />
              <br />
              Contact{' '}
              <Link href="mailto:privacy@NerdWallet.com">
                privacy@NerdWallet.com
              </Link>{' '}
              for help with these requests. Please state that you are a job
              applicant.
              <br />
              <br />
              Any request by you to delete your personal information will not
              result in deletion of any information submitted directly by you to
              a third-party provider (e.g., placement firms etc.) If you require
              the third-party to delete any of your personal information, you
              must contact the third party directly to request such deletion.
            </React.Fragment>
          ),
        },
        {
          title: 'D. Objecting to or requesting the restriction of Processing',
          content: (
            <React.Fragment>
              You can object to, or request the restriction of, the processing
              of your personal information that NerdWallet uses for its
              legitimate interest. Contact{' '}
              <Link href="mailto:privacy@NerdWallet.com">
                privacy@NerdWallet.com
              </Link>{' '}
              for help with these requests.
            </React.Fragment>
          ),
        },
        {
          title: 'E. Data Portability',
          content: (
            <React.Fragment>
              You can move, copy, or transmit personal information you provided
              to NerdWallet for processing related to our performance of your
              employment application. Contact{' '}
              <Link href="mailto:privacy@NerdWallet.com">
                privacy@NerdWallet.com
              </Link>{' '}
              for help with these requests.
            </React.Fragment>
          ),
        },
      ],
    },
    {
      title: '8. Third Party Services',
      content: `As noted above, NerdWallet uses unaffiliated third party service providers to perform services on our behalf, such as Greenhouse for applicant data. The use of these services or websites are governed by the vendor’s privacy policies. To learn more about how data is handled by the application or service provider, please read the applicable privacy policy for each third party service provider.`,
    },
    {
      title: '9. Resolving Concerns/Questions/Contacts',
      content: (
        <React.Fragment>
          If you have any questions or concerns regarding the handling of your
          personal information, please contact your recruiter. Alternatively,
          you may report complaints or concerns to NerdWallet at{' '}
          <Link href="mailto:privacy@NerdWallet.com">
            privacy@NerdWallet.com
          </Link>
          .
        </React.Fragment>
      ),
    },
    {
      title: '10. Changes to Privacy Policy',
      content: `We may change this Privacy Policy at any time. The effective date of each version of this Privacy Policy is identified above.`,
    },
  ],
};
